import moment from "moment-timezone";
import { currentTenantTimezone } from "@/lib/helpers.js";

export type ImportStatus =
  | "needs_mapping"
  | "load_failed"
  | "loading"
  | "ready"
  | "importing"
  | "import_failed"
  | "import_cancelled"
  | "done"
  | "wiped";

export type NextStep =
  | "finish_mapping"
  | "fix_mapping"
  | "review"
  | "start_import"
  | "download_errors";

export type ImportAction =
  | NextStep
  | "run_dry_run"
  | "download_dry_run_report"
  | "download_file"
  | "replace_file"
  | "wipe"
  | "cancel_dry_run"
  | "cancel_import";

export interface HubFile {
  dataset: string;
  label: string;
  href: string;
}

type StringLinks = Partial<
  Record<
    | "review"
    | "import"
    | "dry_run_report"
    | "errors_download"
    | "mapping_page"
    | "replace_file"
    | "wipe"
    | "cancel_dry_run"
    | "cancel_import",
    string
  >
>;

interface Audit {
  started_by?: string | null;
  started_at?: string | null;
  finished_at?: string | null;
  cancel_requested_at?: string | null;
}

// Counts stored on the import run when it finishes; an importing row carries only total/processed.
export interface ImportResults {
  processing?: Record<string, number>;
  results?: Record<string, number>;
  details?: Record<string, number>;
}

export interface ImportRow {
  key: string;
  batch_id: number;
  name: string;
  record_type: string;
  vendor: string;
  rows: number | null;
  created_at: string;
  status: ImportStatus;
  next_step: NextStep | null;
  actions: ImportAction[];
  progress: {
    missing_required?: number;
    load_error?: string;
    // Loading rows carry `loaded` (rows staged so far); importing rows carry total/processed.
    live?: { loaded?: number; total?: number; processed?: number };
    results?: ImportResults | null;
    dry_run?: Audit & {
      state: "running" | "stopping" | "finished" | "stalled" | "cancelled";
      problem_count?: number | null;
    };
    wipe?: Audit;
  };
  import?: Audit;
  links: StringLinks & { files?: HubFile[] };
}

export interface HubTab {
  record_type: string | null;
  count: number;
}

// Datatable text arrives HTML-escaped, so a Vue template would print "&amp;".
export function decodeHtml(value: string | null | undefined): string {
  if (!value) return "";
  return (
    new DOMParser().parseFromString(value, "text/html").body.textContent ?? ""
  );
}

export const TAB_LABELS: Record<string, string> = {
  customers: "Customers",
  invoices: "Invoices",
  coverage_areas: "Coverage areas",
  equipment: "Equipment",
  memberships: "Memberships"
};

export const tabLabel = (recordType: string | null) =>
  recordType === null ? "All" : (TAB_LABELS[recordType] ?? recordType);

export const ACTION_LABELS: Record<ImportAction, string> = {
  finish_mapping: "Finish mapping",
  fix_mapping: "Fix mapping",
  review: "Review",
  start_import: "Start import",
  download_errors: "Download errors",
  run_dry_run: "Run dry run",
  download_dry_run_report: "Download dry run report",
  download_file: "Download file",
  replace_file: "Replace file",
  wipe: "Wipe",
  cancel_dry_run: "Cancel dry run",
  cancel_import: "Cancel import"
};

export const STATUS_CHIPS: Record<
  ImportStatus,
  { label: string; class: string }
> = {
  needs_mapping: {
    label: "Needs mapping",
    class: "bg-amber-50 text-amber-700"
  },
  load_failed: { label: "Load failed", class: "bg-red-50 text-red-700" },
  loading: { label: "Loading", class: "bg-blue-50 text-blue-700" },
  ready: { label: "Ready", class: "bg-green-50 text-green-700" },
  importing: { label: "Importing", class: "bg-blue-50 text-blue-700" },
  import_failed: { label: "Import failed", class: "bg-red-50 text-red-700" },
  import_cancelled: {
    label: "Import cancelled",
    class: "bg-gray-100 text-gray-600"
  },
  done: { label: "Done", class: "bg-green-50 text-green-700" },
  wiped: { label: "Wiped", class: "bg-gray-100 text-gray-600" }
};

type LinkedAction = Exclude<ImportAction, "download_file">;

const LINK_FOR_ACTION: Record<LinkedAction, keyof StringLinks> = {
  review: "review",
  finish_mapping: "mapping_page",
  fix_mapping: "mapping_page",
  start_import: "import",
  run_dry_run: "import",
  download_dry_run_report: "dry_run_report",
  download_errors: "errors_download",
  replace_file: "replace_file",
  wipe: "wipe",
  cancel_dry_run: "cancel_dry_run",
  cancel_import: "cancel_import"
};

export const linkFor = (row: ImportRow, action: LinkedAction) =>
  row.links[LINK_FOR_ACTION[action]];

// One file keeps the plain label; several are told apart by dataset.
export function fileMenuLabel(file: HubFile, fileCount: number) {
  return fileCount === 1
    ? ACTION_LABELS.download_file
    : `Download ${decodeHtml(file.label)} file`;
}

// A stopping dry run is still draining, so it keeps the row polling like a running one.
export const dryRunActive = (state?: string) =>
  state === "running" || state === "stopping";

export const isActive = (row: ImportRow) =>
  row.status === "loading" ||
  row.status === "importing" ||
  dryRunActive(row.progress.dry_run?.state);

export const formatCount = (value: number) => value.toLocaleString("en-US");

export const plural = (count: number, noun: string) =>
  `${formatCount(count)} ${noun}${count === 1 ? "" : "s"}`;

export const formatDateTime = (value: string) =>
  moment(value).tz(currentTenantTimezone).format("MM/DD/YY h:mm a");

export const formatClock = (value: Date) =>
  moment(value).tz(currentTenantTimezone).format("hh:mm a");

export interface ProgressInfo {
  text: string;
  error?: boolean;
  icon?: boolean;
}

const liveProgress = (row: ImportRow, verb: string): ProgressInfo => {
  const live = row.progress.live;
  return {
    text:
      live?.total != null
        ? `${formatCount(live.processed ?? 0)} of ${formatCount(live.total)} ${verb}`
        : ""
  };
};

const loadingProgress = (row: ImportRow): ProgressInfo => {
  const loaded = row.progress.live?.loaded;
  if (loaded == null) return { text: "" };
  return {
    text:
      row.rows == null
        ? `${formatCount(loaded)} loaded`
        : `${formatCount(loaded)} of ${formatCount(row.rows)} loaded`
  };
};

// Rows already running finish after a cancel, so a cancelled import keeps counting up while it stops.
const importingProgress = (row: ImportRow): ProgressInfo => {
  const info = liveProgress(row, "imported");
  if (!row.import?.cancel_requested_at) return info;
  return { text: info.text ? `Stopping: ${info.text}` : "Stopping" };
};

const dryRunProgress = ({ dry_run: dryRun }: ImportRow["progress"]) => {
  if (!dryRun) return { text: "" };
  if (dryRun.state === "running") return { text: "Dry run running" };
  if (dryRun.state === "stopping") return { text: "Dry run stopping" };
  if (dryRun.state === "stalled") return { text: "Dry run didn't finish" };
  if (dryRun.state === "cancelled") return { text: "Dry run cancelled" };
  const problems = dryRun.problem_count ?? 0;
  if (problems > 0) {
    return { text: `Dry run: ${plural(problems, "problem")}`, error: true };
  }
  return { text: "Dry run: no problems" };
};

const resultsProgress = ({ results }: ImportRow["progress"]) => {
  const counts = results?.results;
  if (!counts) return { text: "" };
  const errors = (counts.error ?? 0) + (counts.fatal ?? 0);
  return {
    text: `${formatCount(counts.created ?? 0)} created, ${formatCount(
      counts.updated ?? 0
    )} updated, ${plural(errors, "error")}`
  };
};

const wipedProgress = ({ wipe }: ImportRow["progress"]) => {
  const when = wipe?.finished_at ?? wipe?.started_at;
  const by = decodeHtml(wipe?.started_by);
  return {
    text: ["Wiped", when && formatDateTime(when), by && `by ${by}`]
      .filter(Boolean)
      .join(" ")
  };
};

export function progressInfo(row: ImportRow): ProgressInfo {
  const progress = row.progress ?? {};
  switch (row.status) {
    case "needs_mapping": {
      const missing = progress.missing_required ?? 0;
      return {
        text:
          missing > 0
            ? `${plural(missing, "required field")} not mapped`
            : "Needs mapping"
      };
    }
    case "load_failed":
      return { text: decodeHtml(progress.load_error), error: true, icon: true };
    case "loading":
      return loadingProgress(row);
    case "importing":
      return importingProgress(row);
    case "ready":
      return dryRunProgress(progress);
    case "done":
    case "import_failed":
    case "import_cancelled":
      return resultsProgress(progress);
    case "wiped":
      return wipedProgress(progress);
    default:
      return { text: "" };
  }
}
